import React, { useEffect, useState } from 'react';
import { Trash2, Edit3, X, Save, Loader2, Notebook } from 'lucide-react';
import { portalApi, formatApiError } from '../../contexts/PortalAuthContext';
import { toast } from 'sonner';

export default function PortalJournal() {
  const [entries, setEntries] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await portalApi.get('/journal');
      setEntries(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm('Delete this journal entry?')) return;
    try {
      await portalApi.delete(`/journal/${id}`);
      setEntries(entries.filter(e => e.id !== id));
      toast.success('Entry deleted');
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="portal-journal">
      <header className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-amber-400 text-xs uppercase tracking-widest mb-2">Reflection</div>
          <h1 className="text-3xl font-semibold text-white">Your journal</h1>
          <p className="text-slate-400 mt-2">Capture wins, blockers, and breakthroughs as you work through the programme.</p>
        </div>
        <button
          onClick={() => setEditing({ title: '', content: '', module_id: null })}
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded-lg px-4 py-2 text-sm"
          data-testid="journal-new-btn"
        >
          <Notebook className="w-4 h-4" /> New entry
        </button>
      </header>

      <div className="space-y-4">
        {entries.map(e => (
          <article key={e.id} className="rounded-xl border border-slate-800 bg-slate-900/60 p-5" data-testid={`journal-entry-${e.id}`}>
            <header className="flex items-start justify-between gap-3 mb-2">
              <h3 className="text-lg font-semibold text-white">{e.title}</h3>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => setEditing(e)} className="p-1.5 text-slate-400 hover:text-amber-400 transition" title="Edit">
                  <Edit3 className="w-4 h-4" />
                </button>
                <button onClick={() => remove(e.id)} className="p-1.5 text-slate-400 hover:text-rose-400 transition" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </header>
            <div className="text-xs text-slate-500 mb-3">
              {new Date(e.created_at).toLocaleString()}
            </div>
            <p className="text-slate-300 whitespace-pre-line">{e.content}</p>
          </article>
        ))}
        {entries.length === 0 && (
          <div className="text-slate-500 text-sm">No entries yet — write your first reflection.</div>
        )}
      </div>

      {editing && (
        <JournalEditor
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function JournalEditor({ entry, onClose, onSaved }) {
  const [title, setTitle] = useState(entry.title || '');
  const [content, setContent] = useState(entry.content || '');
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { title, content, module_id: entry.module_id || null };
      if (entry.id) {
        await portalApi.put(`/journal/${entry.id}`, body);
      } else {
        await portalApi.post('/journal', body);
      }
      toast.success('Saved');
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4 py-8" onClick={onClose}>
      <form
        onClick={e => e.stopPropagation()}
        onSubmit={save}
        className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
        data-testid="journal-editor"
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-white">{entry.id ? 'Edit entry' : 'New entry'}</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <input
          required
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Entry title"
          maxLength={140}
          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white mb-3 focus:border-amber-500 outline-none"
          data-testid="journal-edit-title"
        />
        <textarea
          required
          value={content}
          onChange={e => setContent(e.target.value)}
          rows={10}
          placeholder="Write freely…"
          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-white focus:border-amber-500 outline-none resize-y"
          data-testid="journal-edit-content"
        />
        <button
          type="submit"
          disabled={saving}
          className="mt-4 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded-lg px-4 py-2 disabled:opacity-60"
          data-testid="journal-edit-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </form>
    </div>
  );
}
